import { createGlobalState, syncRef } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'

function useReservationCountdown() {
  const settingStore = useSettingStore()
  const checkoutStore = useCheckoutStore()
  const { tables, reservation, is_timer_expired } = storeToRefs(checkoutStore)

  const remainingSeconds = ref<number>(0)
  const isLessThan3Minutes = computed<boolean>(() => remainingSeconds.value < 60 * 3)
  function setRemainingSeconds() {
    if (!reservation.value) return
    const expirationAsMoment = settingStore.parseMoment(reservation.value.expiration)
    const nowAsMoment = settingStore.parseMoment(settingStore.nowDateTime())
    remainingSeconds.value = expirationAsMoment.diff(nowAsMoment, 'seconds')
  }

  let interval: ReturnType<typeof setInterval> | null = null
  function startInterval() {
    if (interval) clearInterval(interval)
    setRemainingSeconds()
    interval = setInterval(setRemainingSeconds, 1000)
  }
  startInterval()
  watch(remainingSeconds, () => {
    if (remainingSeconds.value <= 0 && interval) {
      clearInterval(interval)
      interval = null
    }
  })
  watch(() => reservation.value?.expiration, startInterval)
  onBeforeUnmount(() => {
    if (interval) clearInterval(interval)
  })

  const isExpired = computed<boolean>(() => remainingSeconds.value <= 0)
  const holdingText = computed<string>(() => {
    if (isExpired.value) return 'Hold Expired'
    return tables.value.length > 1 ? 'Holding Tables' : 'Holding Table'
  })
  syncRef(isExpired, is_timer_expired, { direction: 'ltr' })

  const formattedTime = computed<string>(() => {
    if (isExpired.value) return '00:00'
    const minutes = Math.floor(remainingSeconds.value / 60).toString()
    const seconds = (remainingSeconds.value % 60).toString()
    return `${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`
  })

  return {
    remainingSeconds,
    isLessThan3Minutes,
    isExpired,
    holdingText,
    formattedTime,
  }
}

export default createGlobalState(useReservationCountdown)
